// Adapter: derive the LEGACY-shaped calc outputs from a new `ClaimProjection`.
//
// When the `claimProjectionEngine` flag is on for a ported policy whose
// projection succeeded, `runPolicyCalculations` calls this INSTEAD of the
// legacy coverage calc (`calcPolicyProjectedCostCoverage` /
// `calcPolicyCoverageOfRemainingLtcCosts`). Everything downstream — ROI
// (`calcPolicyProjectedROI` reads `client.phaseCalculations`), combined
// rollups (`calcMultiplePolicyTotalFields`, `calcCombined*`), and every
// user-facing surface — runs unchanged on top of the legacy-shaped fields we
// write here.
//
// The reverse mapping (projection path → legacy `SinglePhaseCalculations`
// field + aggregation rule) is documented in `legacyMapping.ts`; this file is
// the executable implementation of that spec. Fields whose legacy entry is
// `null` (genuinely new concepts) are derived explicitly or left at a
// best-effort default.
//
// ─── PARITY TUNING POINTS ─────────────────────────────────────────────────
// The parity-diff harness (`projectionParityDiff.ts`) compares these derived
// fields against the real legacy outputs across traffic. Known spots where the
// legacy semantics are subtle and may need tuning once deltas are observed:
//   • coverage scalar — whether the retroactive EP lump counts toward
//     `projectedCostCoverage` (we include it; see `coverageForMonths`).
//   • `policyInflatedDeathBenefitAmount` — we use the at-issue DB pool; legacy
//     varies by product (flat for CM II, inflated for hybrid life).
//   • `policyTotalLTCBenefit` — we use the last year snapshot's
//     `effectiveTotalLtcBenefit`.
//   • `policyTotalValue` (hybrid life) — INTENTIONAL divergence: we credit LTC
//     actually paid + indemnity surplus + the death-benefit pool actually
//     remaining at horizon (`getTotalPayout`). Legacy `calcHybridPolicyTotalValue`
//     credits the FULL inflated death benefit ("whatever is leftover is paid as
//     DB"), which over-credits unrealized DB. The parity harness will show a
//     delta here for hybrid-life products — that delta is expected, not a
//     regression.
// ──────────────────────────────────────────────────────────────────────────

import { Draft } from 'immer';
import { carePhaseDefs } from '../../../CarePhaseDef';
import { isCareMattersFamily } from '../../../FundingPolicyTypeDef';
import { unlimitedMaxBenefitMagicNumber } from './sharedConstants';
import {
  getCumulativeLtcPayoutSeries,
  getLtcPool,
  getTotalIndemnitySurplus,
  getTotalLtcCoveragePayout,
  getTotalNonLtcPayout,
  getTotalPayout,
} from './projectionTotals';

export type CoverageKey = 'projectedCostCoverage' | 'policyCoverageOfRemainingLtcCosts';

// Single source of truth for which policy field holds "coverage": on-claim
// clients use `policyCoverageOfRemainingLtcCosts`, pre-claim use
// `projectedCostCoverage` (matches `calcMultiplePolicyTotalFields`). The adapter
// (writer), the parity harness, and the engine-diff UI all key off this so they
// can never read a different field than was written.
export function coverageKeyFor(clientHasStartedLtc: boolean): CoverageKey {
  return clientHasStartedLtc ? 'policyCoverageOfRemainingLtcCosts' : 'projectedCostCoverage';
}

// Reads a numeric field off a month's strategy-owned `strategyDeltas` slot
// (CM II line items). Returns 0 when the strategy doesn't emit the field.
function strategyDelta(month: MonthlyEntry, key: string): number {
  const slot = month.deltas.strategyDeltas as Record<string, unknown> | undefined;
  const value = slot?.[key];
  return typeof value === 'number' ? value : 0;
}

function sum(months: MonthlyEntry[], pick: (m: MonthlyEntry) => number): number {
  let total = 0;
  for (const m of months) total += pick(m);
  return total;
}

function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

// Cost-covering payout for a claimant's months: cost-coverage dollars plus any
// retroactive EP lump (which reimburses elimination-period care cost). This is
// the new-engine analogue of the legacy `projectedCostCoverage` accumulator.
function coverageForMonths(months: MonthlyEntry[]): number {
  return getTotalLtcCoveragePayout(months);
}

// Build the sparse `SinglePhaseCalculations[]` for one claimant, indexed by
// `carePhaseDefs[phase].index` exactly like the legacy `updatePhaseCalculations`
// path (ROI's `convertPolicyReturnCashFlow` and the comparison UI both read by
// that index, so dense `push` would misalign).
function buildPhaseCalculations(
  months: MonthlyEntry[],
  yearSnapshots: YearSnapshot[],
  terms: ClaimTerms,
): SinglePhaseCalculations[] {
  const phaseCalcs: SinglePhaseCalculations[] = [];
  if (months.length === 0) return phaseCalcs;

  const snapshotByYear = new Map<number, YearSnapshot>();
  for (const s of yearSnapshots) snapshotByYear.set(s.policyYear, s);

  const cumulativePayout = getCumulativeLtcPayoutSeries(months);
  const indexInMonths = new Map<MonthlyEntry, number>();
  months.forEach((m, i) => indexInMonths.set(m, i));

  // Group preserving chronological order within each phase.
  const byPhase = new Map<CarePhase, MonthlyEntry[]>();
  for (const m of months) {
    const list = byPhase.get(m.context.carePhase) ?? [];
    list.push(m);
    byPhase.set(m.context.carePhase, list);
  }

  for (const [phase, phaseMonths] of byPhase.entries()) {
    const first = phaseMonths[0];
    const last = phaseMonths[phaseMonths.length - 1];

    const phaseYears = Array.from(new Set(phaseMonths.map(m => m.context.policyYear)));
    const phaseSnapshots = phaseYears
      .map(y => snapshotByYear.get(y))
      .filter((s): s is YearSnapshot => s != null);
    const lastSnapshot = phaseSnapshots[phaseSnapshots.length - 1];

    // Per-month monthly cap, resolved against the month's policy year.
    const maxPayoutAccordingToDailyMaximum = sum(
      phaseMonths,
      m => snapshotByYear.get(m.context.policyYear)?.monthlyBenefitCap ?? 0,
    );

    const phaseCostCoverage = sum(phaseMonths, m => m.deltas.payout.ltcCoveragePaid);
    const epReimbursementAmount = sum(phaseMonths, m => strategyDelta(m, 'epLumpSumPaid'));

    const calc: SinglePhaseCalculations = {
      phase,
      // Cost facts (care-cost inputs are shared upstream; surfaced per-phase here).
      phaseMonthlyCost: average(phaseMonths.map(m => m.facts.effectiveCareCost)),
      phaseCostPerDayNeeded: average(phaseMonths.map(m => m.facts.dailyCareCost)),
      phaseCareDaysNeeded: sum(
        phaseMonths,
        m =>
          m.facts.coverageEligibleDays +
          m.facts.non2AdlDays +
          m.facts.waitingPeriodDaysConsumedHome +
          m.facts.waitingPeriodDaysConsumedFacility,
      ),
      daysWhereLessThanTwoADLs: sum(phaseMonths, m => m.facts.non2AdlDays),
      applicableWaitingPeriodDays: sum(
        phaseMonths,
        m => m.facts.waitingPeriodDaysConsumedHome + m.facts.waitingPeriodDaysConsumedFacility,
      ),
      // Legacy: phaseCareDaysNeeded − daysWhereLessThanTwoADLs, i.e. eligible
      // days plus ALL waiting-period days (home AND facility). Dropping the
      // facility WP days understates this for facility-consumed EPs.
      phaseEligibleCareDaysAfter2AdlRequirement: sum(
        phaseMonths,
        m =>
          m.facts.coverageEligibleDays +
          m.facts.waitingPeriodDaysConsumedHome +
          m.facts.waitingPeriodDaysConsumedFacility,
      ),
      phaseEligibleCareDaysAfterWaitingPeriod: sum(phaseMonths, m => m.facts.coverageEligibleDays),
      phaseHomeCareWaitingPeriodDaysRemaining: last.state.waitingPeriod.homeDays,
      phaseFacilityCareWaitingPeriodDaysRemaining: last.state.waitingPeriod.facilityDays,

      // Benefit rates (claim-wide nominal; per-policy-year inflated via snapshots).
      dailyBenefitAmount: terms.nominalDailyBenefit,
      inflationProtection: terms.inflationRate,
      inflationPeriodYears: terms.inflationCapYears,
      inflatedDailyBenefitAmount: average(phaseSnapshots.map(s => s.effectiveDailyBenefit)),
      inflatedFaceAmount: lastSnapshot?.effectiveFaceAmount ?? 0,
      inflatedMaxBenefitAmount: lastSnapshot?.effectiveTotalLtcBenefit ?? 0,
      maxPayoutAccordingToDailyMaximum,

      // Surplus + coverage flows.
      surplusAppliedToCoverage: sum(phaseMonths, m => strategyDelta(m, 'indemnitySurplusApplied')),
      indemnitySurplusForPhase: sum(phaseMonths, m => strategyDelta(m, 'indemnitySurplusAccrued')),
      phaseCostCoverage,
      costCoverageAddedToTotal: sum(phaseMonths, m => m.deltas.payout.ltcPayoutToCost),
      portionOfPhaseCostBenefitEligible: 0,

      // Pool balances (open → close), derived ltcPool = base + eob.
      remainingMaxBenefitBeforePhaseReduction: getLtcPool(first.openState),
      remainingMaxBenefitAfterPhaseReduction: getLtcPool(last.state),
      cumulativePolicyPayOut: cumulativePayout[indexInMonths.get(last) ?? 0] ?? 0,
      indemnityPayout: sum(phaseMonths, m => m.deltas.payout.indemnityPayout),

      // CM II base/rider split (null on products without the concept).
      dailyBenefitAmountBase: terms.nominalDailyBenefitBase || null,
      baseBenefitAmountRemaining: last.state.pools.baseBenefitPool,
      baseAmountApplicableDays: sumOrNull(phaseMonths, m => strategyDelta(m, 'baseBenefitDays')),
      riderAmountApplicableDays: sumOrNull(phaseMonths, m => strategyDelta(m, 'riderBenefitDays')),
      indemnitySurplusBeforePhaseReduction: null,
      policySharedBenefitPoolRemaining: null,
      inflatedDailyBenefitAmountBase: average(phaseSnapshots.map(s => s.effectiveDailyBenefitBase)),
      inflatedDailyBenefitAmountRider: average(
        phaseSnapshots.map(s => s.effectiveDailyBenefitRider),
      ),
      sharedPoolUtilized: null,

      // Elimination-period reimbursement (CM II retroactive lump).
      epReimbursementAmount,
      epDaysReimbursed: sum(phaseMonths, m => strategyDelta(m, 'epLumpSumDays')),
    };

    phaseCalcs[carePhaseDefs[phase].index] = calc;
  }

  return phaseCalcs;
}

// Returns null when every month yields 0, matching the legacy `| null` fields
// that stay null on products without the concept (CM II base/rider days).
function sumOrNull(months: MonthlyEntry[], pick: (m: MonthlyEntry) => number): number | null {
  const total = sum(months, pick);
  return total === 0 ? null : total;
}

// The policy scalar contract the adapter produces for one claimant. Mirrors
// what the legacy coverage calc sets on `ltcPolicy` (the coverage value is
// keyed by claim state — see `CoverageKey`).
export interface LegacyPolicyScalars {
  coverage: number;
  policyTotalValue: number;
  policyNonLtcPayout: number;
  indemnitySurplus: number;
  policyRemainingBenefitAmount: number;
  policyInflatedDeathBenefitAmount: number;
  policyTotalLTCBenefit: number | undefined;
}

export interface LegacyClaimantOutputs {
  phaseCalculations: SinglePhaseCalculations[];
  scalars: LegacyPolicyScalars;
}

// PURE: derive one claimant's legacy-shaped outputs (sparse phaseCalculations +
// policy scalars) from their slice of the projection months. No mutation — used
// both by the mutating adapter below and by the parity-diff harness / tests.
//
// `months` is the claimant's own slice (drives per-claimant outputs:
// phaseCalculations, coverage, remaining benefit). `policyMonths` is the stream
// the POLICY-WIDE scalars are summed over — `policyTotalValue`,
// `policyNonLtcPayout`, `indemnitySurplus`, and the shared death-benefit pool.
// For single-life they're the same stream. For a joint policy the session
// perspective passes the FULL joint projection (both spouses) so the combined
// totals match legacy `calcPolicySummaryStats`, which accumulates both spouses
// (and the shared pool/DB) into one accumulator — summing only the client's
// slice would understate them. Defaults to `months`.
export function deriveLegacyClaimantOutputs(
  projection: ClaimProjection,
  months: MonthlyEntry[],
  policyMonths: MonthlyEntry[] = months,
): LegacyClaimantOutputs {
  const phaseCalculations = buildPhaseCalculations(
    months,
    projection.yearSnapshots,
    projection.terms,
  );
  const lastPhaseCalc = phaseCalculations.filter(Boolean).slice(-1)[0];
  const lastSnapshot = projection.yearSnapshots[projection.yearSnapshots.length - 1];

  return {
    phaseCalculations,
    scalars: {
      coverage: coverageForMonths(months),
      policyTotalValue: getTotalPayout(policyMonths),
      policyNonLtcPayout: getTotalNonLtcPayout(policyMonths),
      indemnitySurplus: getTotalIndemnitySurplus(policyMonths),
      policyRemainingBenefitAmount:
        lastPhaseCalc?.remainingMaxBenefitAfterPhaseReduction ??
        (months.length ? getLtcPool(months[months.length - 1].state) : 0),
      // Death benefit at-issue (shared joint pool); 0 on products without a DB.
      policyInflatedDeathBenefitAmount: policyMonths[0]?.openState.pools.deathBenefit ?? 0,
      // Max lifetime LTC benefit. Products where the snapshot's
      // `effectiveTotalLtcBenefit` is the lifetime pool use it directly; CM II
      // (monthly figure) exposes the true lifetime total on `terms`.
      policyTotalLTCBenefit:
        projection.terms.lifetimeLtcBenefit ?? lastSnapshot?.effectiveTotalLtcBenefit,
    },
  };
}

// Write one claimant's derived outputs onto their client (phaseCalculations)
// and policy (scalar contract). `perspective` selects how much of the contract
// to write, matching legacy `calcPolicyCoverageImpl`:
//   • 'session'  — the full scalar set (lines 1153-1165).
//   • 'partner'  — ONLY coverage + policyRemainingBenefitAmount (lines 1172-1177).
//     Legacy never sets policyTotalValue / policyNonLtcPayout / indemnitySurplus
//     / etc. on the partner; those stay 0. Writing them here would inflate the
//     partner's perspective and (for shared-pool joint products) double-count
//     value the session client already carries.
//   • `policyMonths` — the stream the POLICY-WIDE scalars (total value, non-LTC
//     payout, indemnity surplus, shared DB, CM II base/rider dollars) sum over.
//     Defaults to `months`; the joint session passes the full joint projection
//     so combined totals match legacy. Unused by the 'partner' perspective.
function applyClaimant(
  targetClient: Draft<Client>,
  targetPolicy: Draft<PolicyFundingSource>,
  projection: ClaimProjection,
  months: MonthlyEntry[],
  coverageKey: CoverageKey,
  perspective: 'session' | 'partner',
  policyMonths: MonthlyEntry[] = months,
): void {
  const { phaseCalculations, scalars } = deriveLegacyClaimantOutputs(
    projection,
    months,
    policyMonths,
  );

  targetClient.phaseCalculations = phaseCalculations;
  targetPolicy[coverageKey] = scalars.coverage;
  targetPolicy.policyRemainingBenefitAmount = scalars.policyRemainingBenefitAmount;

  // Mirror legacy's `handleUnlimitedBenefit` side effect: a lifetime pool at
  // the sentinel means unlimited. The whitebox + comparison UIs read this flag
  // (and the `>= sentinel` convention) to render "Unlimited" instead of a raw
  // 999,999,999. Set on both perspectives so a joint unlimited policy flags
  // correctly from either spouse.
  targetPolicy.policyBenefitIsUnlimited =
    (scalars.policyTotalLTCBenefit ?? 0) >= unlimitedMaxBenefitMagicNumber;

  if (perspective === 'partner') return;

  targetPolicy.policyTotalValue = scalars.policyTotalValue;
  targetPolicy.policyNonLtcPayout = scalars.policyNonLtcPayout;
  targetPolicy.indemnitySurplus = scalars.indemnitySurplus;
  targetPolicy.policyInflatedDeathBenefitAmount = scalars.policyInflatedDeathBenefitAmount;
  if (scalars.policyTotalLTCBenefit !== undefined) {
    targetPolicy.policyTotalLTCBenefit = scalars.policyTotalLTCBenefit;
  }

  // CM II death-benefit whitebox: base-pool dollars reduce the death benefit,
  // rider dollars sit on top (informational). Mirrors legacy
  // `calcPolicyCoverageImpl` (gated by `shouldUseCareMattersCalc`):
  // `baseDollarsPaid` accumulates the raw base drawdown (regular base benefit
  // + EP-lump base portion), NOT the floored death-benefit reduction. The
  // projection tracks that exact quantity per row as `base.depletion`.
  if (isCareMattersFamily(targetPolicy.policyType)) {
    targetPolicy.baseDollarsPaid = sum(policyMonths, m => m.deltas.base.depletion);
    targetPolicy.riderDollarsPaid = sum(policyMonths, m => strategyDelta(m, 'riderBenefitPaid'));
  }
}

// Splits a joint projection's claimant-tagged months, or returns the single
// stream for single-life. Exported so the parity harness diffs the same slices.
export function claimantMonths(
  projection: ClaimProjection,
  isJoint: boolean,
): { clientMonths: MonthlyEntry[]; partnerMonths: MonthlyEntry[] } {
  if (!isJoint) {
    return { clientMonths: projection.months, partnerMonths: [] };
  }
  return {
    clientMonths: projection.months.filter(m => m.context.claimant !== 'partner'),
    partnerMonths: projection.months.filter(m => m.context.claimant === 'partner'),
  };
}

export interface ProjectionToLegacyOptions {
  // Selects the coverage field name written on the policy: on-claim clients use
  // `policyCoverageOfRemainingLtcCosts`, pre-claim use `projectedCostCoverage`
  // (matches the `calcMultiplePolicyTotalFields` aggregation branch).
  clientHasStartedLtc: boolean;
  // True for ported joint policies — splits the single joint projection's
  // claimant-tagged months into client- and partner-perspective outputs.
  isJoint: boolean;
}

// Replaces the legacy coverage calc for a ported policy: writes
// `phaseCalculations` + the policy scalar contract from `projection` so the
// unchanged downstream (ROI, combined rollups, UI) sees new-engine numbers.
export function projectionToLegacy(
  mutableClient: Draft<Client>,
  ltcPolicy: Draft<PolicyFundingSource>,
  projection: ClaimProjection,
  options: ProjectionToLegacyOptions,
): void {
  const coverageKey = coverageKeyFor(options.clientHasStartedLtc);

  const partner = mutableClient.mutableClientPartner;
  if (options.isJoint && partner) {
    // The single joint projection carries claimant-tagged months. Split them so
    // each spouse gets their own phaseCalculations + coverage scalars (ROI's
    // cashflow builder reads both clients' phaseCalculations for joint policies).
    const { clientMonths, partnerMonths } = claimantMonths(projection, true);
    // Per-claimant outputs (phaseCalculations, coverage, remaining) come from
    // `clientMonths`; the policy-wide scalars (total value, non-LTC payout,
    // indemnity surplus, shared DB) come from the FULL joint projection so they
    // include the partner's months and the shared pool — matching legacy.
    applyClaimant(
      mutableClient,
      ltcPolicy,
      projection,
      clientMonths,
      coverageKey,
      'session',
      projection.months,
    );
    // Partner policy was mirrored onto the partner by `buildJointProjectionContext`
    // during the (successful) shadow run that produced this projection.
    applyClaimant(
      partner,
      partner.fundingSources.ltcPolicy,
      projection,
      partnerMonths,
      coverageKey,
      'partner',
    );
  } else {
    applyClaimant(mutableClient, ltcPolicy, projection, projection.months, coverageKey, 'session');
  }
}
